import {
  disconnectStripe,
  finishStripeConnect,
  startStripeConnect,
  useGetPaymentSettings,
  useUpdatePaymentSettings,
  type PaymentSettingsOut,
} from '@myrentalmate/api-client';
import {
  Alert,
  Button,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import { okData } from '../../lib/apiData';
import { apiErrorMessage } from '../../lib/apiError';

/** Stripe sends people back here with a one-time code, once only. */
function useFinishConnect() {
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(() =>
    params.get('error') ? 'Your Stripe account wasn’t connected.' : null,
  );
  const started = useRef(false);
  useEffect(() => {
    const code = params.get('code');
    const state = params.get('state');
    if ((!code && !params.get('error')) || started.current) return;
    started.current = true;
    setParams({}, { replace: true }); // the code shouldn't stay in the address or history
    if (!code || !state) return;
    finishStripeConnect({ code, state })
      .then(() => queryClient.invalidateQueries())
      .catch((e: unknown) => setError(apiErrorMessage(e)));
  }, [params, setParams, queryClient]);
  return error;
}

function ConnectSection({ settings }: { settings: PaymentSettingsOut }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const finishError = useFinishConnect();

  async function connect() {
    setBusy(true);
    setError(null);
    try {
      const started = okData(await startStripeConnect());
      if (started) window.location.assign(started.url);
    } catch (e) {
      setError(apiErrorMessage(e));
      setBusy(false);
    }
  }

  async function disconnect() {
    if (!window.confirm('Disconnect this Stripe account? Residents won’t be able to pay online.')) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await disconnectStripe();
      await queryClient.invalidateQueries();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const shown = error ?? finishError;
  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={2} sx={{ alignItems: 'flex-start' }}>
        <Typography variant="h6">Online Payments</Typography>
        <Typography color="text.secondary">
          Residents pay into your own Stripe account, and Stripe pays out to your bank. Connect an
          account you already have, or create one on Stripe&apos;s page. Refunds and disputes are
          handled in your Stripe Dashboard, and your books update by themselves.
        </Typography>
        {shown && <Alert severity="error">{shown}</Alert>}
        {!settings.stripe_available ? (
          <Alert severity="info">Online payments aren&apos;t available yet.</Alert>
        ) : settings.connected ? (
          <>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography sx={{ overflowWrap: 'anywhere' }}>
                Connected: {settings.account_id}
              </Typography>
              {settings.charges_enabled ? (
                <Chip size="small" color="success" label="Ready for Payments" />
              ) : (
                <Chip size="small" color="warning" label="Stripe Needs More Information" />
              )}
            </Stack>
            {!settings.charges_enabled && (
              <Alert severity="warning">
                Residents can&apos;t pay until Stripe finishes checking this account. Sign in to
                your Stripe Dashboard to see what it needs.
              </Alert>
            )}
            <Button color="error" disabled={busy} onClick={() => void disconnect()}>
              Disconnect
            </Button>
          </>
        ) : (
          <Button variant="contained" disabled={busy} onClick={() => void connect()}>
            Connect with Stripe
          </Button>
        )}
      </Stack>
    </Paper>
  );
}

function OptionsSection({ settings }: { settings: PaymentSettingsOut }) {
  const queryClient = useQueryClient();
  const [instructions, setInstructions] = useState(settings.payment_instructions ?? '');
  const save = useUpdatePaymentSettings({
    mutation: { onSuccess: () => queryClient.invalidateQueries() },
  });
  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={2} sx={{ alignItems: 'flex-start' }}>
        <Typography variant="h6">What Residents See</Typography>
        {save.isError && <Alert severity="error">{apiErrorMessage(save.error)}</Alert>}
        <FormControlLabel
          label="Let Residents Pay From a Bank Account"
          control={
            <Switch
              checked={settings.ach_enabled}
              disabled={save.isPending}
              onChange={(e) =>
                save.mutate({
                  data: {
                    ach_enabled: e.target.checked,
                    payment_instructions: settings.payment_instructions,
                  },
                })
              }
            />
          }
        />
        <Typography variant="body2" color="text.secondary">
          Bank payments cost less than cards but take several business days to clear, and can be
          returned afterward. Cards are always offered.
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          label="Other Ways to Pay"
          helperText="Shown in the portal. For example, your Zelle address or where to send a check."
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          slotProps={{ htmlInput: { maxLength: 1000 } }}
        />
        <Button
          variant="outlined"
          disabled={save.isPending}
          onClick={() =>
            save.mutate({
              data: { ach_enabled: settings.ach_enabled, payment_instructions: instructions },
            })
          }
        >
          Save
        </Button>
      </Stack>
    </Paper>
  );
}

export function PaymentSettingsPage() {
  const settings = okData(useGetPaymentSettings().data);
  if (!settings) return null;
  return (
    <Stack spacing={3} sx={{ maxWidth: 720 }}>
      <ConnectSection settings={settings} />
      <OptionsSection settings={settings} />
    </Stack>
  );
}
